import {
  coerceVeeraContextualEntryTransport,
  parseVeeraContextualEntryTransport,
  type VeeraContextualEntryTransport,
  type VeeraThreadOpenerQuestionMode,
} from "./veeraContextualEntry.ts";

type VeeraContextualAssistantMessageSource = {
  carryOverSummary?: string | null;
  whyItMattersNow?: string | null;
  question?: string | null;
  shouldAskQuestion?: boolean;
};

export interface VeeraContextualTopicDescriptor {
  contextualTopicId: string;
  role: VeeraContextualEntryTransport["role"];
  surface: VeeraContextualEntryTransport["surface"];
  threadTitle: string;
  sourceLabel: string;
  carryOverSummary: string;
  whyItMattersNow: string;
  questionMode: VeeraThreadOpenerQuestionMode;
  shouldAskQuestion: boolean;
  question: string | null;
}

function normalizeText(value: string | null | undefined) {
  const normalized = value?.replace(/\s+/g, " ").trim();
  return normalized ? normalized : null;
}

function coerceContextualEntry(value: unknown) {
  if (typeof value === "string") {
    return parseVeeraContextualEntryTransport(value);
  }
  return coerceVeeraContextualEntryTransport(value);
}

export function buildVeeraContextualTopicDescriptor(
  value: unknown,
): VeeraContextualTopicDescriptor | null {
  const contextualEntry = coerceContextualEntry(value);
  if (!contextualEntry) return null;

  const contextualTopicId = normalizeText(contextualEntry.contextualThreadInput?.contextualTopicId);
  const threadTitle = normalizeText(contextualEntry.threadTitle);
  const sourceLabel = normalizeText(contextualEntry.sourceLabel);
  const carryOverSummary = normalizeText(contextualEntry.assistantOpener?.carryOverSummary);
  const whyItMattersNow = normalizeText(contextualEntry.assistantOpener?.whyItMattersNow);
  const questionMode = contextualEntry.assistantOpener?.questionMode ?? null;

  if (!contextualTopicId || !threadTitle || !sourceLabel || !carryOverSummary || !whyItMattersNow || !questionMode) {
    return null;
  }

  return {
    contextualTopicId,
    role: contextualEntry.role,
    surface: contextualEntry.surface,
    threadTitle,
    sourceLabel,
    carryOverSummary,
    whyItMattersNow,
    questionMode,
    shouldAskQuestion: contextualEntry.assistantOpener.shouldAskQuestion === true,
    question: normalizeText(contextualEntry.assistantOpener.question),
  };
}

export function buildVeeraContextualAssistantMessage(
  source: VeeraContextualAssistantMessageSource | null | undefined,
) {
  return [
    source?.carryOverSummary,
    source?.whyItMattersNow,
    source?.shouldAskQuestion ? source?.question : null,
  ]
    .map((value) => normalizeText(value))
    .filter((value): value is string => Boolean(value))
    .join(" ");
}
